import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AccessoryImageTest: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [testResults, setTestResults] = useState<string[]>([]);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [accessoryId, setAccessoryId] = useState<string>('');
  const [accessoryList, setAccessoryList] = useState<Array<{_id: string, name: string}>>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles(filesArray);
      
      // Önizlemeler oluştur
      const newPreviews = filesArray.map(file => URL.createObjectURL(file));
      setPreviews(newPreviews);
    }
  };

  const addTestResult = (result: string) => {
    setTestResults(prev => [...prev, `[${new Date().toISOString()}] ${result}`]);
  };

  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        addTestResult(`Aksesuarları getirme talebi: ${apiUrl}/accessories`);
        const response = await axios.get(`${apiUrl}/accessories`);
        setAccessoryList(response.data.map((a: any) => ({ _id: a._id, name: a.name })));
        addTestResult(`${response.data.length} aksesuar bulundu`);
        
        // İlk aksesuarı seç
        if (response.data.length > 0) {
          setAccessoryId(response.data[0]._id);
          addTestResult(`İlk aksesuar seçildi: ${response.data[0]._id}`);
        }
      } catch (err: any) {
        addTestResult(`Aksesuar listesi getirme hatası: ${err.message}`);
      }
    };
    
    fetchAccessories();
  }, [apiUrl]);

  const testUpload = async () => {
    if (selectedFiles.length === 0) {
      addTestResult('Hata: Yüklenecek dosya seçilmedi');
      return;
    }
    
    if (!accessoryId) {
      addTestResult('Hata: Aksesuar ID\'si belirtilmedi');
      return;
    }

    setUploadStatus('Yükleniyor...');
    addTestResult(`${selectedFiles.length} dosya yükleme testi başlıyor`);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        addTestResult(`Dosya ekleniyor: ${file.name} (${file.size} bytes)`);
        formData.append('images', file);
      });
      
      addTestResult(`API URL: ${apiUrl}/accessories/${accessoryId}/images`);
      const response = await axios.post(
        `${apiUrl}/accessories/${accessoryId}/images`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadStatus(`Yükleniyor: %${percentCompleted}`);
            if (percentCompleted === 100) {
              setUploadStatus('İşleniyor...');
            }
          }
        }
      );
      
      addTestResult(`Başarılı! Sunucu yanıtı: ${JSON.stringify(response.data)}`);
      setUploadStatus('Başarıyla yüklendi');
    } catch (error: any) {
      addTestResult(`Hata: ${error.message}`);
      if (error.response) {
        addTestResult(`Sunucu yanıtı: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
      }
      setUploadStatus('Yükleme başarısız');
    }
  };

  const testDeleteImage = async () => {
    if (!accessoryId) {
      addTestResult('Hata: Aksesuar ID\'si belirtilmedi');
      return;
    }
    
    const imageUrlToDelete = ''; // Silmek istediğiniz görsel URL'sini girin
    if (!imageUrlToDelete) {
      addTestResult('Hata: Silinecek görsel URL\'si belirtilmedi');
      return;
    }
    
    addTestResult(`Görsel silme testi başlıyor: ${imageUrlToDelete}`);
    
    try {
      addTestResult(`API URL: ${apiUrl}/accessories/${accessoryId}/removeImages`);
      const response = await axios.post(`${apiUrl}/accessories/${accessoryId}/removeImages`, {
        images: [imageUrlToDelete]
      });
      
      addTestResult(`Başarılı! Sunucu yanıtı: ${JSON.stringify(response.data)}`);
    } catch (error: any) {
      addTestResult(`Hata: ${error.message}`);
      if (error.response) {
        addTestResult(`Sunucu yanıtı: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Aksesuar Görsel İşleme Testi</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">1. Görsel Yükleme Testi</h2>
        
        <div className="mb-4">
          <label className="block mb-2">Test edilecek aksesuar:</label>
          <select 
            value={accessoryId} 
            onChange={e => setAccessoryId(e.target.value)}
            className="p-2 border rounded w-full"
          >
            <option value="">Aksesuar seçin</option>
            {accessoryList.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name} ({acc._id})</option>
            ))}
          </select>
        </div>

        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileChange}
          className="mb-4"
        />
        
        <div className="flex flex-wrap gap-2 mb-4">
          {previews.map((preview, index) => (
            <div key={index} className="w-24 h-24 relative">
              <img 
                src={preview} 
                alt={`Önizleme ${index + 1}`} 
                className="w-full h-full object-cover border border-gray-300 rounded"
              />
            </div>
          ))}
        </div>
        
        <div className="flex gap-4 mb-4">
          <button
            onClick={testUpload}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Yükleme Testi Yap
          </button>
          
          <button
            onClick={testDeleteImage}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Görsel Silme Testi Yap
          </button>
        </div>
        
        {uploadStatus && (
          <div className="p-3 bg-blue-50 text-blue-700 rounded mb-4">
            {uploadStatus}
          </div>
        )}
      </div>
      
      <div className="bg-gray-100 rounded-lg p-4">
        <h2 className="font-semibold mb-2">Test Sonuçları:</h2>
        <div className="bg-black text-green-400 p-4 rounded font-mono text-sm h-64 overflow-y-auto">
          {testResults.length > 0 ? (
            testResults.map((result, index) => (
              <div key={index}>{result}</div>
            ))
          ) : (
            <div className="text-gray-500">Henüz test yapılmadı</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessoryImageTest;
